import { useState, useRef, useEffect } from "react";
import TextOptionsMenu from "./TextOptionsMenu";
import { MenuSVG } from "./SVG";

const EditableTextArea = ({
  text,
  onTextChange,
  style,
  className,
  font,
  color,
  fontSize = 16,
  onStyleChange,
  isEditable,
  rows = 3,
  isBold,
  isItalic,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [showOptions, setShowOptions] = useState(false);
  const [optionsPosition, setOptionsPosition] = useState({ left: 0, top: 0 });
  const [localText, setLocalText] = useState(text);
  const [buttonPosition, setButtonPosition] = useState("right"); // "right" or "top"
  const editRef = useRef(null);
  const optionsRef = useRef(null);
  const buttonRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    setLocalText(text);
  }, [text]);

  useEffect(() => {
    if (isEditing && editRef.current) {
      editRef.current.focus();
      // Place cursor at the end of the text
      const range = document.createRange();
      const selection = window.getSelection();
      range.selectNodeContents(editRef.current);
      range.collapse(false);
      selection.removeAllRanges();
      selection.addRange(range);
    }
  }, [isEditing]);

  // Check if there's enough space for the button on the right
  useEffect(() => {
    const checkButtonPosition = () => {
      if (containerRef.current && editRef.current) {
        const containerRect = containerRef.current.getBoundingClientRect();
        const rightSpace = window.innerWidth - containerRect.right;

        // If there's less than 40px on the right, position the button on top
        setButtonPosition(rightSpace < 40 ? "top" : "right");
      }
    };

    checkButtonPosition();
    window.addEventListener("resize", checkButtonPosition);
    return () => window.removeEventListener("resize", checkButtonPosition);
  }, []);

  const handleButtonClick = () => {
    if (!buttonRef.current) return;

    const rect = buttonRef.current.getBoundingClientRect();

    // Position the options menu based on button position
    if (buttonPosition === "right") {
      setOptionsPosition({
        top: rect.bottom + 25,
        left: window.innerWidth / 2,
      });
    } else {
      // If button is on top, position menu below the button
      setOptionsPosition({
        top: rect.bottom + 10,
        left: rect.left + rect.width / 2,
      });
    }

    setShowOptions((prev) => !prev);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        showOptions &&
        optionsRef.current &&
        !optionsRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        setShowOptions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showOptions]);

  // Helper function to format text with proper line breaks for display
  const formatTextWithLineBreaks = (text) => {
    if (!text) return "";
    // Replace line breaks with <br> tags for proper HTML rendering
    return text.replace(/\n/g, "<br>");
  };

  const handleBlur = () => {
    setIsEditing(false);
    // Get the raw HTML content with line breaks preserved
    const rawContent = editRef.current.innerHTML;
    // Convert <br> and <div> tags back to newlines for storage
    const normalizedText = rawContent
      .replace(/<br\s*\/?>/gi, "\n")
      .replace(/<div>/gi, "\n")
      .replace(/<\/div>/gi, "")
      .replace(/&nbsp;/gi, " ");

    // Clean up any excessive newlines
    const cleanText = normalizedText
      .replace(/\n{3,}/g, "\n\n") // Replace 3+ consecutive newlines with 2
      .trim();

    if (cleanText !== text) {
      onTextChange(cleanText);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && e.shiftKey) {
      // Shift+Enter to save and exit editing
      e.preventDefault();

      // Get the raw HTML content with line breaks preserved
      const rawContent = editRef.current.innerHTML;
      // Convert <br> and <div> tags back to newlines for storage
      const normalizedText = rawContent
        .replace(/<br\s*\/?>/gi, "\n")
        .replace(/<div>/gi, "\n")
        .replace(/<\/div>/gi, "")
        .replace(/&nbsp;/gi, " ");

      // Clean up any excessive newlines
      const cleanText = normalizedText
        .replace(/\n{3,}/g, "\n\n") // Replace 3+ consecutive newlines with 2
        .trim();

      if (cleanText !== text) {
        onTextChange(cleanText);
      }

      editRef.current.blur();
    }
  };

  const handleInput = (e) => {
    setLocalText(e.target.textContent);
  };

  return (
    <div className="group relative" ref={containerRef}>
      <div
        ref={editRef}
        contentEditable={isEditing}
        onBlur={handleBlur}
        onClick={() => isEditable && setIsEditing(true)}
        onKeyDown={handleKeyDown}
        onInput={handleInput}
        style={{
          ...style,
          fontSize: `${fontSize}px`,
          color,
          whiteSpace: "pre-wrap",
          minHeight: isEditable ? `${rows * 1.5}em` : "auto",
          height: !isEditable ? "auto" : undefined,
          overflowY: "auto",
          padding: "0.5rem",
        }}
        className={`outline-none ${className} ${isBold ? "font-bold" : ""} ${
          isItalic ? "italic" : ""
        } ${isEditing ? "ring-2 ring-teal-500" : ""} ${
          isEditable && !isEditing && "border-2 border-dashed border-gray-200"
        }`}
        suppressContentEditableWarning={true}
        dangerouslySetInnerHTML={{
          __html: isEditing ? text : formatTextWithLineBreaks(text),
        }}
      />

      {isEditable && (
        <button
          type="button"
          ref={buttonRef}
          onClick={handleButtonClick}
          className={`absolute opacity-0 group-hover:opacity-100 transition-opacity bg-teal-600 rounded-full ${
            buttonPosition === "right"
              ? "-right-8 top-0"
              : "left-1/2 -top-8 transform -translate-x-1/2"
          }`}
        >
          <MenuSVG />
        </button>
      )}

      {showOptions && (
        <div
          ref={optionsRef}
          className="fixed bg-white rounded-xl shadow-lg ring-1 ring-black ring-opacity-5 z-50 transform -translate-x-1/2"
          style={{
            left: `${optionsPosition.left}px`,
            top: `${optionsPosition.top}px`,
          }}
        >
          <TextOptionsMenu
            font={font}
            color={color}
            fontSize={fontSize}
            onStyleChange={onStyleChange}
            isBold={isBold}
            isItalic={isItalic}
          />
        </div>
      )}
    </div>
  );
};

export default EditableTextArea;
